import { fetchAuthSession } from 'aws-amplify/auth';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import awsConfig from '../aws-config';

const API_BASE = awsConfig.API.REST.csvReviewApi.endpoint;
const REGION = awsConfig.API.REST.csvReviewApi.region || 'us-west-2';

async function getAuthHeaders() {
  const session = await fetchAuthSession();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session.tokens?.idToken?.toString()}`
  };
}

/**
 * Invoke the Lambda function directly using the AWS SDK (handles SigV4 signing natively).
 * This bypasses the Function URL and calls Lambda:Invoke directly.
 */
async function invokeLambdaDirect(path, body) {
  const session = await fetchAuthSession();
  const credentials = session.credentials;
  if (!credentials) throw new Error('No AWS credentials available. Is the Identity Pool configured?');

  // Extract Cognito user email from the authenticated ID token for audit logging.
  // NOTE: This is from the Cognito-issued JWT (trustworthy), not raw user input.
  // The Lambda should ideally extract identity from the SigV4 caller context instead.
  const email = session.tokens?.idToken?.payload?.email || '';

  const lambda = new LambdaClient({
    region: REGION,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken
    }
  });

  // Build a Function URL-style event so the Lambda handler processes it correctly
  const payload = {
    requestContext: {
      http: { method: 'POST', path: path },
      authorizer: { claims: { email } }
    },
    rawPath: path,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    isBase64Encoded: false
  };

  const command = new InvokeCommand({
    FunctionName: awsConfig.API.lambdaFunctionName,
    Payload: new TextEncoder().encode(JSON.stringify(payload))
  });

  const result = await lambda.send(command);
  const responsePayload = JSON.parse(new TextDecoder().decode(result.Payload));

  if (result.FunctionError) {
    throw new Error(`Lambda error: ${responsePayload.errorMessage || JSON.stringify(responsePayload)}`);
  }

  // The Lambda returns { statusCode, headers, body } — parse the body
  const responseBody = typeof responsePayload.body === 'string'
    ? JSON.parse(responsePayload.body)
    : responsePayload.body || responsePayload;

  if (responsePayload.statusCode && responsePayload.statusCode >= 400) {
    throw new Error(`API error ${responsePayload.statusCode}: ${JSON.stringify(responseBody)}`);
  }

  return responseBody;
}

async function apiRequest(path, options = {}) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers: { ...headers, ...options.headers } });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

// File metadata operations
export async function listFiles(limit = 20, lastKey = null) {
  let path = `/files?limit=${limit}`;
  if (lastKey) path += `&lastKey=${encodeURIComponent(lastKey)}`;
  return apiRequest(path);
}

export async function getFile(fileId) {
  return apiRequest(`/files/${fileId}`);
}

export async function uploadFile(file, approvers, userStory = '') {
  const formData = { fileName: file.name, approvers };
  if (userStory) formData.userStory = userStory;
  const meta = await apiRequest('/files', { method: 'POST', body: JSON.stringify(formData) });
  const uploadResp = await fetch(meta.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': 'text/csv' } });
  if (!uploadResp.ok) throw new Error(`S3 upload failed: ${uploadResp.status} ${uploadResp.statusText}`);
  return meta;
}

export async function getFileContent(fileId) {
  return apiRequest(`/files/${fileId}/content`);
}

export async function saveFileEdits(fileId, csvData) {
  return apiRequest(`/files/${fileId}/content`, {
    method: 'PUT',
    body: JSON.stringify({ data: csvData })
  });
}

export async function saveUserStory(fileId, userStory) {
  return apiRequest(`/files/${fileId}/user-story`, {
    method: 'PUT',
    body: JSON.stringify({ userStory })
  });
}

export async function toggleCodeGen(fileId, enabled) {
  return apiRequest(`/files/${fileId}/codegen-toggle`, {
    method: 'PUT',
    body: JSON.stringify({ enabled })
  });
}

export async function getComments(fileId) {
  return apiRequest(`/files/${fileId}/comments`);
}

export async function addComment(fileId, comment) {
  return apiRequest(`/files/${fileId}/comments`, {
    method: 'POST',
    body: JSON.stringify(comment)
  });
}

export async function resolveComment(fileId, commentId, resolved = true) {
  return apiRequest(`/files/${fileId}/comments/${commentId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ resolved })
  });
}

export async function approveFile(fileId) {
  return apiRequest(`/files/${fileId}/approve`, { method: 'POST' });
}

export async function rejectFile(fileId, reason) {
  return apiRequest(`/files/${fileId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason })
  });
}

export async function approveRows(fileId, rowIndices) {
  return apiRequest(`/files/${fileId}/approve-rows`, {
    method: 'POST',
    body: JSON.stringify({ rowIndices })
  });
}

export async function rejectRows(fileId, rowIndices, reason) {
  return apiRequest(`/files/${fileId}/reject-rows`, {
    method: 'POST',
    body: JSON.stringify({ rowIndices, reason })
  });
}

export async function deleteFile(fileId) {
  return apiRequest(`/files/${fileId}`, { method: 'DELETE' });
}

export async function unapproveFile(fileId) {
  return apiRequest(`/files/${fileId}/unapprove`, { method: 'POST' });
}

export async function addReviewers(fileId, approvers) {
  return apiRequest(`/files/${fileId}/approvers`, {
    method: 'POST',
    body: JSON.stringify({ approvers })
  });
}

export async function exportToTestRail(fileId, payload) {
  return apiRequest(`/files/${fileId}/export-testrail`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

export async function getUserSettings() {
  return apiRequest('/user/settings');
}

export async function saveUserSettings(settings) {
  return apiRequest('/user/settings', {
    method: 'PUT',
    body: JSON.stringify(settings)
  });
}

export async function getReviewsSent() {
  return apiRequest('/user/reviews/sent');
}

export async function getReviewsReceived() {
  return apiRequest('/user/reviews/received');
}

// Test Report dashboard — reads daily CSVs from S3 via the API Gateway
export async function getDashboardDates() {
  return apiRequest('/dashboard/dates');
}

export async function getDashboardReport(date, runId = 72, milestone = '') {
  let path = `/dashboard?date=${encodeURIComponent(date)}&runId=${runId}`;
  if (milestone) path += `&milestone=${encodeURIComponent(milestone)}`;
  return apiRequest(path);
}

// Email recipients (global setting shared across all members)
export async function getEmailRecipients() {
  return apiRequest('/settings/email-recipients');
}

export async function saveEmailRecipients(recipients) {
  return apiRequest('/settings/email-recipients', {
    method: 'PUT',
    body: JSON.stringify({ recipients })
  });
}

// Send bug dashboard report via email (sends pre-built HTML from the displayed dashboard)
export async function sendBugReportEmail(date, htmlBody, bugCount) {
  return apiRequest('/dashboard/send-email-report', {
    method: 'POST',
    body: JSON.stringify({ date, htmlBody, bugCount })
  });
}

// Bedrock test case refinement (chat with LLM to modify generated test cases)
export async function refineTestCases(testCases, instruction, chatHistory = [], signal, userStory = '') {
  const body = { testCases, instruction, chatHistory };
  if (userStory) body.userStory = userStory;
  return invokeLambdaDirect('/refine-testcases', body);
}

// Analyze a code.amazon.com repo — backend clones it and generates a context profile
export async function analyzeRepo(repoName) {
  return invokeLambdaDirect('/analyze-repo', { repoName });
}

// Get the current repo context (latest nightly analysis or static fallback)
export async function getRepoContext() {
  return apiRequest('/repo-context');
}

// Live pipeline health — cached status refreshed from the Pipelines API.
// Returns { pipelines: [{ name, owner, category, group, health, reason, lastUpdated }], oldestUpdated }.
export async function getPipelineHealth() {
  return apiRequest('/pipelines/health');
}

// Trigger a manual re-analysis of the repos
export async function refreshRepoContext(repos) {
  return invokeLambdaDirect('/analyze-repo', { repos: repos || ['HawkeyeAutomationScripts', 'HawkeyeAutomationFramework'] });
}

// Generate test implementation code using repo context
export async function generateTestCode(testCases, mode = 'generate') {
  return invokeLambdaDirect('/generate-test-code', { testCases, mode });
}

// Bedrock test case generation (uses Lambda:Invoke to bypass API Gateway 29s timeout)
export async function generateTestCases(userStory, customInstructions, brdFile, brdFileName) {
  const body = { userStory };
  if (customInstructions) body.customInstructions = customInstructions;
  if (brdFile) body.brdFile = brdFile;
  if (brdFileName) body.brdFileName = brdFileName;
  return invokeLambdaDirect('/generate-testcases', body);
}

/**
 * Streaming test case generation — reads NDJSON from Lambda Function URL.
 * Calls onStatus/onTestCase/onDone/onError callbacks as chunks arrive.
 * Falls back to non-streaming invokeLambdaDirect if Function URL is not configured.
 */
export async function generateTestCasesStream(userStory, { onStatus, onTestCase, onDone, onError, signal } = {}) {
  const fnUrl = awsConfig.API.generateTestCasesFnUrl;
  if (!fnUrl) {
    // Fallback to non-streaming
    if (onStatus) onStatus('Generating (non-streaming mode)...');
    const result = await generateTestCases(userStory);
    if (result.testCases) {
      result.testCases.forEach((tc, i) => onTestCase && onTestCase(i, tc));
      if (onDone) onDone(result.testCases.length);
    }
    return result.testCases || [];
  }

  const session = await fetchAuthSession();
  const credentials = session.credentials;
  if (!credentials) throw new Error('No AWS credentials available');

  // Sign the request with SigV4 for IAM-authenticated Function URL
  const { SignatureV4 } = await import('@smithy/signature-v4');
  const { Sha256 } = await import('@aws-crypto/sha256-js');
  const { HttpRequest } = await import('@smithy/protocol-http');

  const url = new URL(fnUrl + '/generate-testcases-stream');
  const body = JSON.stringify({ userStory });

  const request = new HttpRequest({
    method: 'POST',
    hostname: url.hostname,
    path: url.pathname,
    headers: {
      host: url.hostname,
      'content-type': 'application/json'
    },
    body
  });

  const signer = new SignatureV4({
    service: 'lambda',
    region: 'us-west-2',
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken
    },
    sha256: Sha256
  });

  const signed = await signer.sign(request);
  const headers = {};
  for (const [k, v] of Object.entries(signed.headers)) {
    headers[k] = v;
  }

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers,
    body,
    signal
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Stream request failed: ${response.status} ${errText}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const testCases = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Process complete NDJSON lines
    const lines = buffer.split('\n');
    buffer = lines.pop() || ''; // Keep incomplete last line in buffer

    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const msg = JSON.parse(line);
        if (msg.type === 'status' && onStatus) onStatus(msg.message);
        if (msg.type === 'testcase') {
          testCases.push(msg.data);
          if (onTestCase) onTestCase(msg.index, msg.data);
        }
        if (msg.type === 'done' && onDone) onDone(msg.total);
        if (msg.type === 'error') {
          if (onError) onError(msg.message);
          else throw new Error(msg.message);
        }
      } catch (e) {
        if (!(e instanceof SyntaxError)) throw e;
        // Skip malformed JSON lines
      }
    }
  }

  // Flush any remaining data in the buffer after stream ends
  if (buffer.trim()) {
    try {
      const msg = JSON.parse(buffer);
      if (msg.type === 'testcase') {
        testCases.push(msg.data);
        if (onTestCase) onTestCase(msg.index, msg.data);
      } else if (msg.type === 'done' && onDone) {
        onDone(msg.total);
      } else if (msg.type === 'error') {
        if (onError) onError(msg.message);
        else throw new Error(msg.message);
      }
    } catch (e) {
      if (!(e instanceof SyntaxError)) throw e;
    }
  }

  return testCases;
}

export async function getSmokeSanityDashboard() {
  return apiRequest('/dashboard/smoke-sanity');
}

export async function getBugHygieneDashboard() {
  return apiRequest('/dashboard/bug-hygiene');
}
